import { useCallback, useRef, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  useRateHouseholdChatMessageMutation,
  useSubmitHouseholdChatFeedbackMutation,
} from '@/modules/household/chat/graphql/HouseholdChatFeedback.generated';
import { HouseholdChatFeedbackRating } from '@/types/schema';

export type ChatFeedbackRating = 'good' | 'bad';

export function useChatFeedback({ messageId }: { messageId: string }) {
  const { showFeedback } = useFeedback();
  const [saveRating, { loading: isSavingRating }] =
    useRateHouseholdChatMessageMutation();
  const [saveComment] = useSubmitHouseholdChatFeedbackMutation();
  const ratingRequestInFlight = useRef(false);
  const [rating, setRating] = useState<ChatFeedbackRating | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const rateMessage = useCallback(
    async (newRating: ChatFeedbackRating) => {
      if (ratingRequestInFlight.current) {
        return;
      }
      if (newRating === rating) {
        if (newRating === 'bad') {
          setIsFeedbackModalOpen(true);
        }
        return;
      }

      ratingRequestInFlight.current = true;
      try {
        await saveRating({
          variables: {
            messageID: messageId,
            rating:
              newRating === 'good'
                ? HouseholdChatFeedbackRating.Good
                : HouseholdChatFeedbackRating.Bad,
          },
        });
        setRating(newRating);
        if (newRating === 'bad') {
          setIsFeedbackModalOpen(true);
        } else {
          showFeedback('Thanks for the feedback!', { variant: 'success' });
        }
      } catch {
        showFeedback('Could not save your rating. Please try again.', {
          variant: 'error',
        });
      } finally {
        ratingRequestInFlight.current = false;
      }
    },
    [messageId, rating, saveRating, showFeedback]
  );

  const provideFeedback = useCallback(
    async (feedbackText: string) => {
      const result = await saveComment({
        variables: { messageID: messageId, feedback: feedbackText },
      });
      showFeedback('Thanks for the feedback!', { variant: 'success' });
      return result;
    },
    [messageId, saveComment, showFeedback]
  );

  const closeFeedbackModal = useCallback(() => {
    setIsFeedbackModalOpen(false);
  }, []);

  return {
    rating,
    rateMessage,
    isSavingRating,
    isFeedbackModalOpen,
    closeFeedbackModal,
    provideFeedback,
  };
}
